import React from 'react';
import type { SanityBlock } from '@sanity/client';
import styled from '@emotion/styled';
import { spacing } from '@naf/theme';
import { themeLight } from '@lib/nafTheme';
import { NumberCircle } from '@naf-simba/elements-numbers';
import { Text, TextVariant } from '@naf/text';
import { ArticleBlockContentGridCol } from './ArticleBlockContentGridCol';
import BlockImage from './BlockImage';
import BlockContent from '../../../BlockContent';
import { CloudinaryImageType } from '../../../../../../../types/CloudinaryImageType';
import { BlockContentContext } from '../../../BlockContent.utils';

interface Props {
  value: { numberedList?: { image: CloudinaryImageType; body: SanityBlock[]; title: string }[]; title: string };
}

export const NumberedList = ({ value: { numberedList } }: Props) => (
  <Wrapper className="numberedList">
    {numberedList?.map(({ image, body, title }, index) => (
      <ListItem key={title}>
        <StyledNumber size="small" value={index + 1} />
        <HeaderText variant={TextVariant.ArticleTextLargeHeader}>{title}</HeaderText>
        <ContentWrapper>
          <BlockImage image={image} />
          <BlockContent value={body} />
        </ContentWrapper>
      </ListItem>
    ))}
  </Wrapper>
);

function NumberedListSerializer(context: BlockContentContext = BlockContentContext.Stripped) {
  if (context === BlockContentContext.Article) {
    return ({ value }: Props) => (
      <ArticleBlockContentGridCol>
        <NumberedList value={value} />
      </ArticleBlockContentGridCol>
    );
  }
  return ({ value }: Props) => <NumberedList value={value} />;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.space16};
`;

const ListItem = styled.div`
  display: grid;
  grid-template-columns: ${spacing.space24} auto;
  gap: ${spacing.space16};

  & p,
  figure,
  h4 {
    margin: 0;
  }

  padding-top: ${spacing.space16};
  padding-bottom: ${spacing.space16};
  border-top: 1px solid ${({ theme }) => theme.border.subtle || themeLight.border.subtle};
`;

const HeaderText = styled(Text)`
  grid-row: 1;
  grid-column: 2;
`;

const ContentWrapper = styled.div`
  grid-column: 2/ -1;
`;

const StyledNumber = styled(NumberCircle)`
  grid-row: 1;
  grid-column: 1;
  align-self: center;
`;

export default NumberedListSerializer;
